import React, { useCallback, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { rem } from 'polished'

import { mediaQueryDown } from 'components/utils/breakpoint'
import Slider from 'LuxKit/Slider/Slider'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import TextLink from 'LuxKit/TextLink'
import Tooltip from 'LuxKit/Tooltip'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineSyncIcon from 'LuxKit/Icons/line/LineSyncIcon'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import getLoyaltyProgramDisplayName from 'loyalty/selectors/getLoyaltyProgramDisplayName'
import LoyaltyTierIcon from 'LuxKit/Icons/loyalty/LoyaltyTierIcon'
import LoadingBox from 'components/Common/Loading/LoadingBox'
import { InfoIconButton, SmallSwitch } from 'loyalty/components/LoyaltyBurnSliderPrimitives'

const Container = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  border-radius: ${props => props.theme.borderRadius.M};
  padding: ${rem(14.5)} ${rem(20)};
  box-shadow: ${props => props.theme.shadow.flat.small};

  &[data-rounded] {
    border-radius: ${props => props.theme.borderRadius.round};
  }

  ${mediaQueryDown.mobile} {
    border: 1px solid ${props => props.theme.palette.neutral.default.four};
    border-radius: 0;
    padding: ${rem(12)} ${rem(16)};
    box-shadow: none;

    &[data-rounded] {
      border: 1px solid ${props => props.theme.palette.neutral.default.five};
      border-radius: ${props => props.theme.borderRadius.round};
    }
  }
`

const SliderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${rem(12)};
  grid-row: 1;
  min-width: 0;
`

const HeadingGroup = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
`

const HeadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};
`

const RoundedMobileSubtitle = styled.div`
  display: none;

  ${mediaQueryDown.mobile} {
    display: block;
  }
`

const HeaderRight = styled.div<{ $wrongRegion?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
  flex-shrink: 0;

  ${props => props.$wrongRegion && css`
    ${mediaQueryDown.mobile} {
      display: none;
    }
  `}
`

const DesktopBalanceAndApplied = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(4)};

  ${mediaQueryDown.mobile} {
    display: none;
  }
`

const MobileBalanceText = styled.div`
  display: none;

  ${mediaQueryDown.mobile} {
    display: block;
  }
`

const Grid = styled.div<{ $hasIcon: boolean }>`
  display: grid;
  grid-template-columns: ${props => props.$hasIcon ? `${rem(32)} 1fr` : '1fr'};
  grid-template-rows: auto auto;
  column-gap: ${rem(24)};
  align-items: center;

  ${mediaQueryDown.mobile} {
    column-gap: ${rem(12)};
  }
`

const MobileWrongRegionContent = styled.div`
  display: none;

  ${mediaQueryDown.mobile} {
    display: flex;
    flex-direction: column;
    gap: ${rem(2)};
    margin-top: ${rem(2)};
  }
`

const MobileWrongRegionRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(8)};
`

const GridIcon = styled(LoyaltyTierIcon)`
  grid-row: 1;
  align-self: start;
`

const SliderCell = styled.div<{ $hasIcon: boolean }>`
  grid-row: 2;
  ${props => props.$hasIcon && css`grid-column: 2;`}
  min-width: 0;

  ${mediaQueryDown.mobile} {
    grid-column: 1 / -1;
    grid-row: 3;
  }
`

const MobileBurnApplied = styled.div`
  display: none;

  ${mediaQueryDown.mobile} {
    display: flex;
    align-items: baseline;
    grid-column: 1 / -1;
    grid-row: 2;
    margin-top: ${rem(12)};
    padding-top: ${rem(12)};
    border-top: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
`

const SMALL_THUMB = 32

const SmallSlider = styled(Slider)`
  height: ${rem(SMALL_THUMB)};
  touch-action: none;

  input {
    &::-webkit-slider-thumb {
      height: ${rem(SMALL_THUMB)};
      width: ${rem(SMALL_THUMB)};
    }
    &::-moz-range-thumb {
      height: ${rem(SMALL_THUMB)};
      width: ${rem(SMALL_THUMB)};
    }
    &::-ms-thumb {
      height: ${rem(SMALL_THUMB)};
      width: ${rem(SMALL_THUMB)};
    }
  }
`

const ValueLabel = styled.span`
  position: absolute;
  bottom: 100%;
  left: var(--label-position);
  transform: translateX(-50%);
  margin-bottom: ${rem(4)};
  padding: ${rem(2)} ${rem(6)};
  background-color: ${props => props.theme.palette.neutral.default.one};
  color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${props => props.theme.borderRadius.S};
  font-size: ${rem(12)};
  white-space: nowrap;
  pointer-events: none;
  opacity: var(--label-visible);
  transition: opacity 0.15s;
`

const SliderContainer = styled.div`
  margin-top: ${rem(4)};

  ${mediaQueryDown.mobile} {
    margin-top: 0;
  }
`

const SliderInputContainer = styled.div`
  position: relative;
  padding-top: ${rem(8)};
`

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: ${rem(4)};
`

const MinimumRequiredRow = styled.div`
  margin-top: ${rem(4)};
`

interface Props {
  isEnabled: boolean
  onToggle: () => void
  tier: App.LoyaltyTier | undefined
  hasLoyaltyAccount: boolean
  userPoints: number
  maxBurnPoints: number
  minBurnPoints: number
  localValue: number
  onSliderChange: (e: React.ChangeEvent<HTMLInputElement>, value: number) => void
  onReset: () => void
  onInfoClick: () => void
  rounded?: boolean
  loading?: boolean
  wrongRegion?: boolean
  switchRegionPath?: string
  onSwitchRegion?: (() => void) | undefined
  loyaltyCurrencyLabel?: string
}

function LoyaltyBurnSliderBase(props: Props) {
  const {
    isEnabled,
    onToggle,
    tier,
    hasLoyaltyAccount,
    userPoints,
    maxBurnPoints,
    minBurnPoints,
    localValue,
    onSliderChange,
    onReset,
    onInfoClick,
    rounded,
    loading,
    wrongRegion,
    switchRegionPath,
    onSwitchRegion,
    loyaltyCurrencyLabel,
  } = props

  const intl = useIntl()

  const showLoyaltyCard = !!tier
  const hasSliderRange = maxBurnPoints > minBurnPoints
  const showMaxPointsTooltip = maxBurnPoints < userPoints
  const remainingBalance = isEnabled ? userPoints - localValue : userPoints

  const [labelVisible, setLabelVisible] = useState(false)

  const labelPosition = maxBurnPoints > minBurnPoints
    ? ((localValue - minBurnPoints) / (maxBurnPoints - minBurnPoints)) * 100
    : 0

  const sliderMax = maxBurnPoints
  const sliderValue = Math.min(localValue, sliderMax)

  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onSliderChange(e, Math.min(e.currentTarget.valueAsNumber, maxBurnPoints))
  }, [onSliderChange, maxBurnPoints])

  const showValueLabel = useCallback(() => setLabelVisible(true), [])
  const hideValueLabel = useCallback(() => setLabelVisible(false), [])

  const maxPointsTooltipDescription = intl.formatMessage({
    id: 'iZRfOcbQIUcd4MZrkvQX',
    defaultMessage: 'The maximum points is based on the highest priced room and package within your selected dates.',
    description: 'Tooltip explaining why maximum redeemable points may be less than user balance',
  })

  let switchRegionCta: React.ReactNode = null
  if (wrongRegion) {
    switchRegionCta = <>
      <Caption variant="medium" colour="normal-contrast">
        <FormattedMessage
          id="GBH0wDkFQFp96byoFWjN"
          defaultMessage="To use your points"
          description="Prompt shown in burn slider when user is browsing outside their loyalty account region"
        />
      </Caption>
      <TextLink size="medium" endIcon={<LineSyncIcon size="XS" />} onClick={onSwitchRegion} href={switchRegionPath}>
        <FormattedMessage
          id="N_ufYqvQN3hfk5C-ia_Q"
          defaultMessage="Switch to {currency}"
          description="Link to switch to the user's loyalty account region"
          values={{ currency: loyaltyCurrencyLabel }}
        />
      </TextLink>
    </>
  }

  const balanceCaption = (points: number) => (
    <Caption variant="medium" colour="normal-contrast">
      <FormattedMessage
        id="IybuZyVzgiAs9iGI9VOB"
        defaultMessage="Your balance: {points} pts"
        description="Loyalty points balance shown in the burn slider header"
        values={{ points: intl.formatNumber(points) }}
      />
    </Caption>
  )

  return (
    <Container data-rounded={rounded || undefined}>
      <Grid $hasIcon={showLoyaltyCard}>
        {showLoyaltyCard && <GridIcon tier={tier ?? 'gold'} size="L" />}
        <SliderHeader>
          <HeadingGroup>
            <HeadingRow>
              <BodyText variant="medium" weight="bold">
                <FormattedMessage
                  id="cl0x-dmWuCnOIjq9g-s2"
                  defaultMessage="Use {programName} points to pay"
                  description="Title for the loyalty program points burn slider component"
                  values={{ programName: getLoyaltyProgramDisplayName() }}
                />
              </BodyText>
              <InfoIconButton
                onClick={onInfoClick}
                aria-label={intl.formatMessage(
                  {
                    id: 'tB8M5ogsc_sJY6Y3f75S',
                    defaultMessage: 'Learn more about {programName} points',
                    description: 'Accessibility label for loyalty program points info button on the burn slider',
                  },
                  { programName: getLoyaltyProgramDisplayName() },
                )}
              >
                <LineInfoCircleIcon size="XS" />
              </InfoIconButton>
            </HeadingRow>
            {!wrongRegion && !rounded && hasLoyaltyAccount && <MobileBalanceText>
              {balanceCaption(remainingBalance)}
            </MobileBalanceText>}
            {rounded && <RoundedMobileSubtitle>
              {isEnabled && <Caption variant="medium" colour="normal-contrast" weight="bold">
                <FormattedMessage
                  id="6Exob_owYtfuvbQ5N-pE"
                  defaultMessage="{points} pts applied!"
                  description="Points applied confirmation shown on rounded slider mobile"
                  values={{ points: intl.formatNumber(localValue) }}
                />
              </Caption>}
              {!isEnabled && hasLoyaltyAccount && balanceCaption(remainingBalance)}
            </RoundedMobileSubtitle>}
            {wrongRegion && <MobileWrongRegionContent>
              <MobileWrongRegionRow>
                {switchRegionCta}
              </MobileWrongRegionRow>
            </MobileWrongRegionContent>}
          </HeadingGroup>
          <HeaderRight $wrongRegion={wrongRegion}>
            {wrongRegion && switchRegionCta}
            {!wrongRegion && <>
              <DesktopBalanceAndApplied>
                {isEnabled && !rounded && <>
                  {balanceCaption(remainingBalance)}
                  {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                  <Caption variant="medium" colour="normal-contrast">·</Caption>
                  <Caption variant="medium" weight="bold">
                    <FormattedMessage
                      id="ogjs6nruB3TD51e6aPiu"
                      defaultMessage="{points} pts applied"
                      description="Points currently applied count shown next to the toggle switch in desktop header"
                      values={{ points: intl.formatNumber(localValue) }}
                    />
                  </Caption>
                </>}
                {isEnabled && rounded && <Caption variant="medium" weight="bold">
                  <FormattedMessage
                    id="6Exob_owYtfuvbQ5N-pE"
                    defaultMessage="{points} pts applied!"
                    description="Points applied confirmation shown on rounded slider mobile"
                    values={{ points: intl.formatNumber(localValue) }}
                  />
                </Caption>}
                {!isEnabled && hasLoyaltyAccount && balanceCaption(remainingBalance)}
              </DesktopBalanceAndApplied>
              <SmallSwitch
                checked={isEnabled}
                onChange={onToggle}
                aria-label={intl.formatMessage({
                  id: 'CHTRwDYdC64aqr2BgjNY',
                  defaultMessage: 'Toggle points usage',
                  description: 'Accessibility label for the points burn toggle switch in slider header',
                })}
              />
            </>}
          </HeaderRight>
        </SliderHeader>

        {isEnabled && !rounded && <MobileBurnApplied>
          <BodyText variant="medium">
            <FormattedMessage
              id="5hbsD6VENtQWkRoPLQ_t"
              defaultMessage="{points} pts applied"
              description="Points applied label shown on mobile below the slider header"
              values={{
                points: intl.formatNumber(localValue),
              }}
            />
          </BodyText>
        </MobileBurnApplied>}

        {!wrongRegion && isEnabled && (loading || hasSliderRange) && (
          <SliderCell $hasIcon={showLoyaltyCard}>
            <SliderContainer>
              {loading && <LoadingBox height={16} borderRadius="XS" />}
              {!loading && <>
                <SliderInputContainer
                  onPointerDown={showValueLabel}
                  onPointerUp={hideValueLabel}
                  onPointerLeave={hideValueLabel}
                  onFocus={showValueLabel}
                  onBlur={hideValueLabel}
                >
                  <ValueLabel style={{ '--label-position': `${labelPosition}%`, '--label-visible': labelVisible ? 1 : 0 } as React.CSSProperties}>
                    {intl.formatNumber(localValue)}
                  </ValueLabel>
                  <SmallSlider
                    min={minBurnPoints}
                    max={sliderMax}
                    step={1}
                    value={sliderValue}
                    onChange={handleSliderChange}
                    style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                    aria-label={intl.formatMessage({
                      id: '2GfiSdssClH7ydudqUze',
                      defaultMessage: 'Select points to apply',
                      description: 'Accessibility label for the range slider to select burn points amount',
                    })}
                  />
                </SliderInputContainer>
                <SliderLabels>
                  <Caption variant="medium" colour="light-foreground-alt">
                    <FormatLoyaltyPoints points={minBurnPoints} abbreviated />
                  </Caption>
                  {showMaxPointsTooltip && <Tooltip
                    description={maxPointsTooltipDescription}
                    placement="top-end"
                  >
                    <Caption variant="medium">
                      <FormatLoyaltyPoints points={maxBurnPoints} abbreviated />
                      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                      {' '}
                      <LineInfoCircleIcon size="XS" />
                    </Caption>
                  </Tooltip>}
                  {!showMaxPointsTooltip && <Caption variant="medium">
                    <FormatLoyaltyPoints points={maxBurnPoints} abbreviated />
                  </Caption>}
                </SliderLabels>
                <MinimumRequiredRow>
                  <Caption variant="medium" colour="light-foreground-alt">
                    <FormattedMessage
                      id="rT1y6cqQRywo2arpqIjH"
                      defaultMessage="Minimum {points} required"
                      description="Message showing minimum points required to start redeeming in the slider"
                      values={{
                        points: <FormatLoyaltyPoints points={minBurnPoints} abbreviated />,
                      }}
                    />
                    {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
                    {' · '}
                    <TextLink size="caption" onClick={onReset}>
                      <FormattedMessage
                        id="7TkjKqnvA3E1Ai0MOGIP"
                        defaultMessage="Reset"
                        description="Button to reset the points selection back to zero in the burn slider"
                      />
                    </TextLink>
                  </Caption>
                </MinimumRequiredRow>
              </>}
            </SliderContainer>
          </SliderCell>
        )}
      </Grid>
    </Container>
  )
}

export default LoyaltyBurnSliderBase
